import React from 'react'
import { Link } from 'react-router-dom'
import { useCmsLanding, authMarketingFeatures } from '../../hooks/useCmsLanding'
import { RsIcon } from '../icons'

const FALLBACK_FEATURES = [
  { icon: 'lock', text: 'Secure platform for GP/LP deal workflow' },
]

export function AuthLogo({ className = '' }) {
  return (
    <div className={`auth-logo ${className}`}>
      Rotation<span>Street</span>
    </div>
  )
}

/** Login / Forgot / Verify — blue gradient + white right panel */
export function AuthSplitLayout({
  title,
  description,
  features: featuresProp,
  marketingPanel = 'login',
  children,
  footer,
}) {
  const { cms } = useCmsLanding()
  const cmsFeatures = authMarketingFeatures(cms, marketingPanel)
  const features = featuresProp?.length ? featuresProp : (cmsFeatures.length ? cmsFeatures : FALLBACK_FEATURES)
  const panel = cms?.authMarketing?.[marketingPanel] || {}
  const displayTitle = title ?? panel.title
  const displayDescription = description ?? panel.description
  return (
    <div className="auth-page">
      <div className="auth-left">
        <h2>{displayTitle}</h2>
        <p>{displayDescription}</p>
        <div className="auth-left-features">
          {features.map((f) => (
            <div key={f.text} className="auth-lf-item">
              <span className="auth-lf-icon"><RsIcon name={f.icon} size="sm" /></span>
              {f.text}
            </div>
          ))}
        </div>
      </div>
      <div className="auth-right">
        <div className="auth-box">
          {children}
          {footer}
        </div>
      </div>
    </div>
  )
}

/** Sign up — left branding + right form (prototype signup.html) */
export function AuthSignupLayout({ children, stepLabel, headerExtra }) {
  const { cms } = useCmsLanding()
  const features = authMarketingFeatures(cms, 'signup').length
    ? authMarketingFeatures(cms, 'signup')
    : FALLBACK_FEATURES
  const signupPanel = cms?.authMarketing?.signup || {}
  return (
    <div className="auth-signup-page">
      <div className="auth-signup-left">
        <h2>
          {signupPanel.title || (
            <>
              Join the Global
              <br />
              China Investment Network
            </>
          )}
        </h2>
        <p>
          {signupPanel.description ||
            'Access exclusive China-focused investment opportunities from verified GPs. Multilingual support, secure escrow, end-to-end deal management.'}
        </p>
        <div className="auth-left-features" style={{ marginTop: 36, gap: 14 }}>
          {features.map((f) => (
            <div key={f.text} className="auth-lf-item" style={{ fontSize: 13.5 }}>
              <span className="auth-lf-icon" style={{ width: 30, height: 30, fontSize: 14 }}>
                <RsIcon name={f.icon} size="sm" />
              </span>
              {f.text}
            </div>
          ))}
        </div>
      </div>
      <div className="auth-signup-right">
        <div className="auth-box auth-box--signup">
          <div className="auth-signup-header">
            <div>
              <div className="auth-logo" style={{ fontSize: 24, marginBottom: 4 }}>
                Rotation<span>Street</span>
              </div>
              {stepLabel && <div className="auth-subtitle" style={{ marginBottom: 0 }}>{stepLabel}</div>}
            </div>
            {headerExtra || (
              <div className="auth-footer-link" style={{ marginTop: 0, textAlign: 'right' }}>
                Already have an account?
                <br />
                <Link to="/login">Login →</Link>
              </div>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

export function AuthProgressSteps({ steps, current }) {
  return (
    <div className="auth-progress">
      {steps.map((label, i) => {
        const n = i + 1
        const done = n < current
        const active = n === current
        return (
          <div
            key={label}
            className={`auth-ps-step${active ? ' active' : ''}${done ? ' done' : ''}`}
          >
            <div className="auth-ps-num">{done ? '✓' : n}</div>
            <div className="auth-ps-label">{label}</div>
          </div>
        )
      })}
    </div>
  )
}

export function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="#0077b5" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

export function WeChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="#07c160" aria-hidden>
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.139.045c.134 0 .24-.111.24-.245 0-.06-.023-.118-.038-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-7.062-6.122zm-2.877 2.588c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.97-.982z" />
    </svg>
  )
}
